import { EmptyState } from '@/components/common/EmptyState';
import { SkeletonBlock } from '@/components/common/Skeleton';
import { CARD } from '@/components/common/surfaces';
import { CreateProjectCard } from '@/components/projects/CreateProjectCard';
import { ProjectCard } from '@/components/projects/ProjectCard';
import type { ProjectListItem } from '@/types/project.types';
import type { RuleSetListItem } from '@/types/ruleSet.types';
import { FolderOpen } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/** One page of projects, so the skeleton fills the grid the data is about to fill. */
const SKELETON_COUNT = 6;

/** The grid both the real cards and their placeholders sit in. */
const GRID_CLASSES = 'grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3';

interface ProjectGridProps {
  projects: ProjectListItem[];
  isLoading: boolean;
  onProjectClick: (projectId: string) => void;
  onUserClick?: (ownerId: string) => void;
  selectedProjectIds?: Set<string>;
  onProjectSelect?: (projectId: string, selected: boolean) => void;
  onStartUpload?: (file: File) => void;
  /** Rule set a project created from this grid is machined with. */
  holeRuleSet?: string | null;
  /** Every rule set the backend offers, so cards can show a project's set by title. */
  ruleSets?: RuleSetListItem[];
}

/**
 * A placeholder in the shape of a `ProjectCard`.
 *
 * Same card, same 4 px padding, same 16:9 preview and the same three lines under it, so the grid
 * does not reflow when the projects arrive. A centred spinner over an empty 400 px box — what was
 * here before — moved everything on the page once the data landed.
 */
const ProjectCardSkeleton: FC = () => (
  <div className={`${CARD} flex flex-col p-1`} aria-hidden="true">
    <SkeletonBlock className="aspect-video w-full rounded-xl" />
    <div className="flex flex-col gap-2 px-3 pb-3 pt-3">
      <SkeletonBlock className="h-4 w-40" />
      <SkeletonBlock className="h-3 w-32" />
      <SkeletonBlock className="h-3 w-24" />
    </div>
  </div>
);

export const ProjectGrid: FC<ProjectGridProps> = ({
  projects,
  isLoading,
  onProjectClick,
  onUserClick,
  selectedProjectIds,
  onProjectSelect,
  onStartUpload,
  holeRuleSet = null,
  ruleSets = [],
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={GRID_CLASSES}>
        {Array.from({ length: SKELETON_COUNT }, (_, index) => index).map((index) => (
          <ProjectCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className={GRID_CLASSES}>
        <CreateProjectCard onStartUpload={onStartUpload} holeRuleSet={holeRuleSet} />
        <div className={`${CARD} md:col-span-1 lg:col-span-2`}>
          <EmptyState
            icon={<FolderOpen size={28} aria-hidden="true" />}
            title={t('projects.noProjects')}
            message={t('projects.noProjectsMessage')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={GRID_CLASSES}>
      <CreateProjectCard onStartUpload={onStartUpload} holeRuleSet={holeRuleSet} />
      {projects.map((project) => (
        <ProjectCard
          key={project.id}
          project={project}
          onClick={onProjectClick}
          onUserClick={onUserClick}
          isSelected={selectedProjectIds?.has(project.id) ?? false}
          onSelect={onProjectSelect}
          ruleSets={ruleSets}
        />
      ))}
    </div>
  );
};
